/**
 * Two levels of account, and the line between them.
 *
 * A broker gets the answer. A superuser gets the answer plus the machinery that produced
 * it: every lender swept, every channel, the coverage line, the retrieved guideline
 * sections, and the lender's own PDF page behind each citation. That second view is a
 * troubleshooting instrument - it is how a retrieval miss is told apart from a synthesis
 * miss - and it was being rendered to everyone because there was only ever one view.
 *
 * Both predicates live here, in one file, on purpose. The API and the web app were going
 * to answer the same question separately - the API deciding whether to serve a page and
 * the UI deciding whether to draw the button that asks for it - and two copies of an
 * authorization rule is how the button ends up hidden over a route that still serves.
 *
 * Note what is NOT here: any notion of trimming the answer itself. The engine sweeps
 * every lender and synthesis writes the whole answer object for a broker exactly as it
 * does for us. See slots.ts, which makes the same argument at length: a zero result is
 * only a proof if nothing was trimmed before it was counted, and the eval suite scores
 * the full answer. This module decides what is RENDERED and what is SERVED, never what
 * is computed.
 */

/** Broker is the default and the common case; superuser is the two of us. */
export type AccountLevel = 'broker' | 'superuser';

/**
 * The two staff flags, which `AccountLevel` deliberately does not carry.
 *
 * `accountLevel` collapses an account to broker-or-superuser and ignores `isAdmin`
 * entirely, because every question it answers is about what a BROKER may see. The
 * metrics report is the first question that is about staff, and staff is both flags.
 */
export interface StaffAccount {
  readonly isAdmin: boolean;
  readonly isSuperuser: boolean;
}

/**
 * May this account read the metrics report?
 *
 * ADMIN **OR** SUPERUSER, and the "or" is the whole point. Henry Liner, 2026-09-16: "are
 * we tracking view ae contact button ... This will be useful metrics for when I talk to
 * banks ... so at any time we can run a report like: How many view Ae contact clicks did
 * Logan get in September". The report is for him, and his row is `is_admin = false,
 * is_superuser = true` - checked, not assumed. Gating it on `isAdmin` alone would ship a
 * report the person who asked for it cannot open.
 *
 * It is a separate predicate rather than a loosening of the admin rule because the two
 * powers are separate and migration 046 argues that out at length: `isAdmin` is power over
 * the corpus - the cascade delete, the review queue - and widening it to admit a superuser
 * would hand Henry the delete button in order to show him a table of counts.
 *
 * Here rather than in either app, for the reason the file exists: the API preHandler asks
 * it before serving `/api/admin/metrics` and App.tsx asks it before drawing the nav
 * button, so the button and the 403 cannot disagree. Two copies of an authorization rule
 * is how a hidden button ends up over a route that still answers.
 */
export function canReadMetrics(user: StaffAccount): boolean {
  return user.isAdmin || user.isSuperuser;
}

/** The shape both the API's session user and the web app's `User` already satisfy. */
export interface LevelledAccount {
  readonly isSuperuser: boolean;
}

export function accountLevel(user: LevelledAccount): AccountLevel {
  return user.isSuperuser ? 'superuser' : 'broker';
}

/**
 * May this account open the source document a citation points at?
 *
 * EVERY SIGNED-IN ACCOUNT MAY. Misha, 2026-09-08: "for both superuser and 'demo' user and
 * regular user, they should be able to see the matrix PDF stuff.. so change whatever u
 * need, upload whatever u need so that shit is viewable."
 *
 * This reverses the original split, and the argument it reverses is worth keeping rather
 * than deleting, because it was a real argument: a rendered PDF page is the lender's
 * document rather than our claim about it, the citation alone (lender, document, section,
 * page, quoted sentence) is a complete attribution, and opening the page was framed as an
 * audit instrument for us. What that reasoning got wrong is who the audit is for. A broker
 * reading "Arc caps you at 80% here" has no way to tell a correct retrieval from a wrong
 * one, and the product's whole claim is that the answer rests on the lender's own page -
 * a claim only the page itself can settle. Withholding it made the claim unfalsifiable
 * for exactly the person being asked to act on it.
 *
 * STILL A FUNCTION, AND STILL ASKED IN BOTH PLACES. It returns true for every level today,
 * and this is deliberately not deleted: the API preHandler on `/api/citation/:id/page.png`
 * asks it, `routes/matrix.ts` asks it before including a lender's verbatim `body_md`, and
 * the web app asks it before drawing the viewer. One predicate, three callers, so the
 * button and the route can never disagree - and one line to narrow again if a lender ever
 * asks us to.
 *
 * `level` is kept in the signature for the same reason. It is unused today; the moment it
 * is not, every caller already passes it.
 *
 * WHETHER, NOT HOW MANY. This predicate says a broker may open the page; it says nothing
 * about opening every page of every lender in an afternoon, and since accounts became a
 * phone number and a code that distinction started to matter. The count lives beside the
 * renderer - a per-account daily page allowance in apps/api/src/routes/citation.ts,
 * PAGE_VIEW_DAILY_LIMIT - because it is a rate, not an authorization, and putting it here
 * would make a pure predicate the web app calls depend on a counter only the server has.
 */
export function canOpenSourceDocuments(_level: AccountLevel): boolean {
  return true;
}

/**
 * May this account see the sweep - the per-lender grid, the coverage line, the list of
 * retrieved guideline sections?
 *
 * PRESENTATION ONLY, and deliberately not enforced on the wire. The `verdicts` frame
 * still carries the full lender roster to a broker's browser, because the findings are
 * keyed by lenderId and that roster is the only thing that maps a UUID to a name -
 * withhold it server-side and every lender card loses the lender, which is the product's
 * entire job. So this hides a display, and it is honest about being a display decision.
 *
 * That is a different judgement from `canOpenSourceDocuments`, and the difference is
 * what the two protect. A rendered PDF page is content a broker was not given; the sweep
 * is our own bookkeeping about a search they ran. Nothing is lost if a broker reads it
 * out of the network tab - it is just not what they came for.
 */
export function showsInternalSweep(level: AccountLevel): boolean {
  return level === 'superuser';
}
